import type { EnvironmentPreset, RenderObject, Vec3 } from '@v2/contracts'

/** Center Y is the playing surface. Dressing never owns semantic game objects. */
export interface EnvironmentBounds {
  readonly center: Vec3
  readonly width: number
  readonly depth: number
}

const WATERLINE = -0.72
const TAU = Math.PI * 2
const palette = {
  rock: ['#727970', '#85887b', '#a1a28c', '#646e69'],
  asteroid: ['#71748b', '#555d77', '#89869a', '#636779'],
}

/** The only clock-dependent state is a slow, deterministic environmental phase. */
export function buildEnvironmentObjects(
  preset: EnvironmentPreset,
  bounds: EnvironmentBounds,
  timeSeconds: number,
): readonly RenderObject[] {
  if (preset === 'simple') return []
  const clock = Number.isFinite(timeSeconds) ? timeSeconds : 0
  const objects: RenderObject[] = []
  const [cx, cy, cz] = bounds.center
  const halfWidth = bounds.width / 2
  const halfDepth = bounds.depth / 2

  const add = (
    id: string, shape: RenderObject['shape'], position: Vec3, size: Vec3,
    color: string, rotation = 0, roughness = 0.78,
  ): void => {
    objects.push({
      id: `__env:${preset}:${id}`, shape,
      position: [cx + position[0], cy + position[1], cz + position[2]],
      size, color, rotation, roughness, interactive: false, decorative: true,
    })
  }

  if (preset === 'ocean') {
    // Overlapping faceted stone skirts hide the severe rectangular lower edge.
    // Their tops sit below the playing surface and their toes meet the water.
    const horizontal = Math.max(5, Math.min(9, Math.ceil(bounds.width / 2.5)))
    const vertical = Math.max(4, Math.min(7, Math.ceil(bounds.depth / 2.5)))
    for (const side of [-1, 1]) {
      for (let i = 0; i < horizontal; i++) {
        const x = (i + 0.5) / horizontal * bounds.width - halfWidth
        add(`coast:z:${side}:${i}`, 'rock', [x, -0.45, side * (halfDepth - 0.1)],
          [bounds.width / horizontal * 1.19, 0.7 + hash(i + side * 5) * 0.12, 0.7 + hash(i * 3) * 0.22],
          palette.rock[(i + (side + 1)) % palette.rock.length], Math.sin(i * 8.2) * 0.15)
      }
      for (let i = 0; i < vertical; i++) {
        const z = (i + 0.5) / vertical * bounds.depth - halfDepth
        add(`coast:x:${side}:${i}`, 'rock', [side * (halfWidth - 0.11), -0.45, z],
          [0.76 + hash(i * 2) * 0.18, 0.7 + hash(i + 7) * 0.1, bounds.depth / vertical * 1.18],
          palette.rock[(i + 1) % palette.rock.length], Math.sin(i * 4.2) * 0.12)
      }
    }

    // A tiny planted outcrop gives the coast scale without decorating play mats.
    const isletX = -halfWidth * 0.55
    const isletZ = -halfDepth - 0.67
    add('islet:base', 'rock', [isletX, WATERLINE + 0.1, isletZ], [1.5, 0.46, 1.15], '#828b76', 0.3)
    add('islet:grass', 'rock', [isletX, WATERLINE + 0.32, isletZ], [0.99, 0.12, 0.73], '#738f58', 0.3)
    add('islet:stone', 'rock', [isletX + 0.56, WATERLINE + 0.015, isletZ + 0.1], [0.38, 0.25, 0.42], '#bbc0a8')
    for (let i = 0; i < 2; i++) {
      const x = isletX - 0.19 + i * 0.34
      const z = isletZ + (i === 0 ? -0.1 : 0.09)
      add(`islet:tree:${i}:trunk`, 'disc', [x, WATERLINE + 0.6, z], [0.06, 0.58, 0.06], '#695d40')
      add(`islet:tree:${i}:canopy`, 'sphere', [x, WATERLINE + 0.86 - i * 0.08, z], [0.48, 0.62, 0.46], i === 0 ? '#426f50' : '#73924d')
    }

    // Small weathered jetty; individual boards catch the sun, posts meet the sea.
    const dockZ = halfDepth * 0.23
    for (let i = 0; i < 5; i++) {
      add(`dock:plank:${i}`, 'slab', [halfWidth + 0.13 + i * 0.235, -0.15, dockZ],
        [0.221, 0.065, 0.77], i % 2 ? '#8f8670' : '#a3967d', 0, 0.85)
    }
    for (const x of [halfWidth + 0.12, halfWidth + 1.09]) for (const z of [-0.32, 0.32]) {
      add(`dock:post:${x}:${z}`, 'disc', [x, -0.405, dockZ + z], [0.095, 0.79, 0.095], '#6e5b3e')
    }

    // Boats travel only a few centimeters each second, with independent 50–65s arcs.
    boat('cutter', [-halfWidth - 0.96, WATERLINE + 0.035, halfDepth * 0.27], 0.18, 0, 0.96)
    boat('sloop', [halfWidth + 0.98, WATERLINE + 0.035, -halfDepth * 0.47], Math.PI - 0.12, 2.1, 0.81)
  } else {
    // A quiet machined edge, with fine mint seams rather than bright pulsing lights.
    const rimY = -0.24
    for (const side of [-1, 1]) {
      add(`frame:z:${side}`, 'slab', [0, rimY, side * (halfDepth + 0.03)], [bounds.width + 0.18, 0.22, 0.19], '#52627c', 0, 0.43)
      add(`frame:x:${side}`, 'slab', [side * (halfWidth + 0.03), rimY, 0], [0.19, 0.22, bounds.depth + 0.18], '#52627c', 0, 0.43)
      add(`seam:z:${side}`, 'slab', [0, rimY + 0.115, side * (halfDepth + 0.03)], [bounds.width * 0.74, 0.013, 0.045], '#80b9bb', 0, 0.7)
    }
    for (const x of [-1, 1]) for (const z of [-1, 1]) {
      add(`corner:${x}:${z}`, 'slab', [x * (halfWidth - 0.08), -0.245, z * (halfDepth - 0.08)], [0.48, 0.28, 0.48], '#768397', Math.PI / 4, 0.4)
    }

    // The asteroid families remain behind/outside the playable rectangle.
    for (let i = 0; i < 9; i++) {
      const side = i % 2 ? -1 : 1
      const x = side * (halfWidth + 0.57 + hash(i + 4) * 0.5)
      const rawZ = (hash(i * 6 + 1) - 0.5) * bounds.depth * 0.87
      // Leave clean negative space around the shuttle's recognizable silhouette.
      const z = side === 1 && rawZ > halfDepth * 0.15 ? rawZ - halfDepth * 0.62 : rawZ
      const size = 0.2 + hash(i + 19) * 0.43
      const phase = clock * TAU / (78 + i * 3) + i * 2.1
      add(`asteroid:${i}`, 'rock', [x, -0.28 + Math.sin(phase) * 0.045, z],
        [size, size * (0.72 + hash(i) * 0.32), size * 0.89], palette.asteroid[i % 4], i + clock * 0.004, 0.94)
    }
    satellite('satellite', [-halfWidth * 0.37, 0.04, -halfDepth - 0.89], -0.14, clock)
    shuttle('shuttle', [halfWidth + 0.97, -0.03, halfDepth * 0.38], -0.16, clock)
  }

  return objects

  function boat(id: string, origin: Vec3, heading: number, phase: number, scale: number): void {
    const drift = clock * TAU / (phase === 0 ? 58 : 67) + phase
    const yaw = heading + Math.sin(drift) * 0.055
    const location: Vec3 = [origin[0] + Math.sin(drift) * 0.035, origin[1] + Math.sin(clock * TAU / 7.8 + phase) * 0.025, origin[2] + Math.sin(drift) * 0.22]
    const part = (partId: string, shape: RenderObject['shape'], offset: Vec3, size: Vec3, color: string, extraYaw = 0, roughness = 0.78): void => {
      const rotated = rotate(offset, yaw)
      add(`${id}:${partId}`, shape, [location[0] + rotated[0] * scale, location[1] + offset[1] * scale, location[2] + rotated[2] * scale],
        [size[0] * scale, size[1] * scale, size[2] * scale], color, yaw + extraYaw, roughness)
    }
    part('hull', 'hull', [0, 0.02, 0], [0.85, 0.3, 1.91], '#453f33')
    part('gunwale', 'hull', [0, 0.17, 0], [0.88, 0.05, 1.93], '#807762')
    part('deck', 'hull', [0, 0.198, 0], [0.76, 0.025, 1.71], '#6f5d48')
    part('cabin', 'slab', [0, 0.29, 0.4], [0.42, 0.18, 0.45], '#bcb9a2')
    part('cabin-roof', 'slab', [0, 0.39, 0.4], [0.47, 0.04, 0.51], '#514b3f')
    part('mast', 'disc', [0, 0.91, -0.23], [0.044, 1.44, 0.044], '#735939')
    // Swing the boom around the mast so the fabric reads as a full white sail
    // from the initial camera, while the narrow hull still clears the play area.
    const sailAngle = 0.62
    const boom = rotate([0, 0.39, 0.43], sailAngle)
    const mainSail = rotate([0.015, 0.975, 0.455], sailAngle)
    part('boom', 'slab', [boom[0], boom[1], boom[2] - 0.23], [0.035, 0.035, 0.91], '#807158', sailAngle)
    part('main-sail', 'sail', [mainSail[0], mainSail[1], mainSail[2] - 0.23], [0.4, 1.15, 0.88], '#f1f0e7', sailAngle, 0.93)
    part('jib', 'sail', [-0.02, 0.895, -0.53], [0.31, 0.93, 0.55], '#e0e5df', Math.PI, 0.94)
  }

  function satellite(id: string, origin: Vec3, heading: number, seconds: number): void {
    const bob = Math.sin(seconds * TAU / 42) * 0.035
    const part = (partId: string, shape: RenderObject['shape'], offset: Vec3, size: Vec3, color: string, roughness = 0.52): void => {
      const rotated = rotate(offset, heading)
      add(`${id}:${partId}`, shape, [origin[0] + rotated[0], origin[1] + bob + offset[1], origin[2] + rotated[2]], size, color, heading, roughness)
    }
    part('core', 'slab', [0, 0, 0], [0.43, 0.32, 0.5], '#dddcc8')
    part('foil', 'slab', [0, -0.1, 0], [0.46, 0.13, 0.52], '#b49c64')
    part('crossbar', 'slab', [0, 0, 0], [1.77, 0.06, 0.06], '#8995a5')
    for (const side of [-1, 1]) {
      part(`panel-frame:${side}`, 'slab', [side * 0.65, 0, 0], [0.72, 0.055, 0.71], '#7c899f')
      part(`panel:${side}`, 'slab', [side * 0.65, 0.032, 0], [0.65, 0.019, 0.64], '#355581', 0.66)
      for (let stripe = -1; stripe <= 1; stripe++) {
        part(`panel-grid:${side}:${stripe}`, 'slab', [side * 0.65 + stripe * 0.18, 0.043, 0], [0.009, 0.005, 0.64], '#82a3b3', 0.9)
      }
    }
    part('dish', 'sphere', [0, 0.21, 0.02], [0.28, 0.1, 0.28], '#f0ede0')
    part('antenna', 'disc', [0, 0.39, 0.02], [0.017, 0.3, 0.017], '#a5b9c8')
  }

  function shuttle(id: string, origin: Vec3, heading: number, seconds: number): void {
    const bob = Math.sin(seconds * TAU / 54 + 1.8) * 0.035
    const part = (partId: string, shape: RenderObject['shape'], offset: Vec3, size: Vec3, color: string, roughness = 0.6): void => {
      const rotated = rotate(offset, heading)
      add(`${id}:${partId}`, shape, [origin[0] + rotated[0], origin[1] + bob + offset[1], origin[2] + rotated[2]], size, color, heading, roughness)
    }
    part('fuselage', 'hull', [0, 0, 0], [0.47, 0.27, 1.3], '#ced4d3')
    part('cockpit', 'sphere', [0, 0.15, -0.15], [0.3, 0.18, 0.5], '#40667a', 0.32)
    part('wings', 'hull', [0, -0.035, 0.21], [1.04, 0.075, 0.75], '#acb9c5')
    part('tail', 'slab', [0, 0.2, 0.45], [0.075, 0.36, 0.29], '#8fa2ad')
    for (const side of [-1, 1]) {
      part(`engine:${side}`, 'slab', [side * 0.2, -0.01, 0.53], [0.18, 0.17, 0.31], '#5d6b7b')
      part(`exhaust:${side}`, 'slab', [side * 0.2, -0.01, 0.691], [0.12, 0.11, 0.024], '#8ed1db', 0.8)
    }
  }
}

function rotate(position: Vec3, yaw: number): Vec3 {
  const c = Math.cos(yaw)
  const s = Math.sin(yaw)
  return [c * position[0] + s * position[2], position[1], -s * position[0] + c * position[2]]
}

function hash(value: number): number {
  const number = Math.sin(value * 19.17 + 7.71) * 143.32
  return number - Math.floor(number)
}
